import {
  Alert,
  Button,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { convert } from "portal/utils/units/units";
import {
  getCarbonCropsPath,
  getRobotPath,
  RobotSubpath,
} from "portal/utils/routing";
import {
  getRobotCropMode,
  RobotCropSystem,
} from "portal/views/robots/crops/robotCropMode";
import { Link, Navigate, useParams } from "react-router-dom";
import { portalEndpoints } from "portal/state/portalApi";
import { RobotCarbonCropList } from "portal/views/robots/crops/RobotCarbonCropList";
import { RobotVeselkaCropList } from "portal/views/robots/crops/RobotVeselkaCropList";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useCanSeeCarbonCropsPage,
  useCanUpdateCarbonCropsPage,
} from "portal/views/carbonCrops/permissions";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useShadowValue } from "portal/utils/hooks/useShadowValue";
import { useTranslation } from "react-i18next";
import CarbonCropsIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import React, { FC, useMemo } from "react";

const _RobotCropsPage: FC = () => {
  const { t } = useTranslation();
  const { serial, cropId } = useParams();

  const canSeeCarbonCropsPage = useCanSeeCarbonCropsPage();
  const canUpdateCarbonCrops = useCanUpdateCarbonCropsPage();

  const { data: summary, isFetching: isSummaryFetching } = useQueryPopups(
    portalEndpoints.getRobot.useQuery(
      serial
        ? {
            serial,
            includeCustomer: true,
            includeHealthLogs: false,
            includeDailyMetrics: false,
          }
        : skipToken,
      {
        pollingInterval: convert(1).from("min").to("ms"),
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
      }
    ),
    { errorVariant: "warning" }
  );

  const mode = useMemo(
    () =>
      getRobotCropMode({
        featureFlags: summary?.robot?.featureFlags,
      }),
    [summary?.robot?.featureFlags]
  );

  const showToggle = mode.allowViewToggle && canSeeCarbonCropsPage;
  const [view, setView] = useShadowValue(mode.active);
  const effectiveView: RobotCropSystem = showToggle ? view : mode.active;
  const robotCustomerUuid = summary?.customer?.uuid;
  const showManageCarbonCrops =
    effectiveView === RobotCropSystem.CarbonCrop &&
    canUpdateCarbonCrops &&
    Boolean(robotCustomerUuid);

  const cropsBasePath = getRobotPath(serial, RobotSubpath.CROPS);
  if (effectiveView === RobotCropSystem.CarbonCrop && cropId) {
    return <Navigate to={cropsBasePath} replace />;
  }

  if (isSummaryFetching && !summary) {
    return (
      <div className="flex h-full w-full flex-col gap-3 p-4">
        <Skeleton variant="rounded" className="h-12 w-full" />
        <Skeleton variant="rounded" className="h-9 w-64" />
        <Skeleton variant="rounded" className="min-h-0 flex-1 w-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      {canSeeCarbonCropsPage && (
        <div className="flex flex-col gap-3">
          <Alert severity="info">
            {mode.active === RobotCropSystem.CarbonCrop
              ? t("views.robots.crops.activeBannerCarbon")
              : t("views.robots.crops.activeBannerVeselka")}
          </Alert>
          {showToggle && (
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={effectiveView}
              onChange={(_event, next: RobotCropSystem | null) => {
                if (next) {
                  setView(next);
                }
              }}
            >
              <ToggleButton value={RobotCropSystem.VeselkaCrop}>
                {t("views.robots.crops.veselkaCrops")}
              </ToggleButton>
              <ToggleButton value={RobotCropSystem.CarbonCrop}>
                {t("views.carbonCrops.title")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </div>
      )}
      {showManageCarbonCrops && robotCustomerUuid && (
        <div className="flex justify-end">
          <Button
            component={Link}
            to={getCarbonCropsPath(robotCustomerUuid)}
            variant="contained"
            size="small"
            color="secondary"
            startIcon={<CarbonCropsIcon />}
          >
            {t("views.robots.crops.goToCarbonCrops")}
          </Button>
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        {effectiveView === RobotCropSystem.CarbonCrop ? (
          serial && robotCustomerUuid ? (
            <RobotCarbonCropList
              serial={serial}
              robotFeatureFlags={summary.robot?.featureFlags}
            />
          ) : (
            <Alert severity="warning" className="m-4">
              {t("views.robots.crops.missingCustomer")}
            </Alert>
          )
        ) : (
          <RobotVeselkaCropList />
        )}
      </div>
    </div>
  );
};

export const RobotCropsPage = _RobotCropsPage;
